import React, {PropsWithChildren, useState} from 'react';
import Client from '@guestapp/sdk';
import {useQuery} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {useReservation} from './reservation';
import Storage from '../utils/storage';
import {
  CHEKIN_PRO_API_KEY_NAME,
  MAIN_TOKEN_ID_KEY,
  QUERY_CACHE_KEYS,
  RESERVATION_ID_KEY,
  SHARE_LINK,
} from '../utils/constants';

type ContextProps = Client['reservation'] &
  Client['guest'] &
  Client['identityVerification'] &
  Client['customForm'] &
  Client['validations'] &
  Client['locations'] &
  Client['guestFormAdditionalData'] &
  Client['housing'] &
  Client['guestGroup'] &
  Client['documents'] &
  Client['payments'] &
  Client['websocket'] &
  Client['upselling'] &
  Client['propertyProtection'] &
  Client['allianzInsurance'] &
  Client['locks'] &
  Client['taxes'] & {
    isTokenValid: boolean;
    isAuthInitialization: boolean;
    isAuthInitializationError: boolean;
    authError?: Error;
    isSuccessSDK: boolean;
    reservationId?: string;
    housingId?: string;
    mainToken?: string | null;
    hasToken?: boolean;
  };

const ChekinSDKContext = React.createContext<ContextProps>({} as ContextProps);

function ChekinSDKProvider({children}: PropsWithChildren) {
  const {t} = useTranslation();
  const chekinProApiKey = Storage.get(CHEKIN_PRO_API_KEY_NAME) || 'FALSO_TOKEN'; //for dev purpose

  // const currentReservationId = Storage.get(RESERVATION_ID_KEY);
  // const reservationId =
  //   currentReservationId?.toString() || '449dc172189f45e58a15d59e222aaf3f';

  const [, , , token] = window.location.href.split('/');
  const chekinSdk = React.useMemo(
    () =>
      new Client({
        apikey: chekinProApiKey,
        // reservationId: reservationId,
        isChekinConsumer: true,
        maxRequestsRetriesCount: 0,
        token,
      }),
    [chekinProApiKey, token],
  );

  const {initChekin} = chekinSdk.auth;
  const [reservationId, setReservationId] = useState<string | undefined>(undefined);
  const {housingId} = useReservation();
  const {
    data,
    error,
    isLoading: isAuthInitialization,
    isLoadingError: isAuthInitializationError,
    isSuccess: isSuccessSDK,
  } = useQuery<{object_id: string; url_path: string}, Error>(
    [QUERY_CACHE_KEYS.apiClient],
    () => initChekin(token),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      enabled: Boolean(token),
      refetchOnReconnect: false,
    },
  );

  React.useEffect(() => {
    if (data) {
      setReservationId(data?.object_id);
      Storage.set(RESERVATION_ID_KEY, data?.object_id);
    }
  }, [data, error]);

  const isTokenValid = Boolean(data);

  const authError = React.useMemo<Error | undefined>(() => {
    if (!isAuthInitializationError) return;
    if (!error) return {name: 'cors_dns', message: t('dns_is_forbidden_error_message')};
    return error;
  }, [error, isAuthInitializationError, t]);

  const mainToken = React.useMemo(() => {
    if (token) {
      Storage.set(MAIN_TOKEN_ID_KEY, token);
      Storage.set(SHARE_LINK, `${process.env.PUBLIC_URL}/${token}`);
    }

    return token || Storage.get(MAIN_TOKEN_ID_KEY);
  }, [token]);

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      ...chekinSdk.identityVerification,
      ...chekinSdk.locations,
      ...chekinSdk.guest,
      ...chekinSdk.reservation,
      ...chekinSdk.taxes,
      ...chekinSdk.guestFormAdditionalData,
      ...chekinSdk.guestGroup,
      ...chekinSdk.customForm,
      ...chekinSdk.housing,
      ...chekinSdk.validations,
      ...chekinSdk.documents,
      ...chekinSdk.payments,
      ...chekinSdk.websocket,
      ...chekinSdk.upselling,
      ...chekinSdk.propertyProtection,
      ...chekinSdk.allianzInsurance,
      ...chekinSdk.locks,
      isTokenValid,
      isAuthInitialization,
      isAuthInitializationError,
      authError,
      reservationId,
      mainToken,
      isSuccessSDK,
      housingId,
      hasToken: Boolean(token),
    }),
    [
      authError,
      chekinSdk,
      housingId,
      isAuthInitialization,
      isAuthInitializationError,
      isSuccessSDK,
      isTokenValid,
      mainToken,
      reservationId,
      token,
    ],
  );

  return <ChekinSDKContext.Provider value={defaultProps} children={children} />;
}

function useChekinSDK() {
  const context = React.useContext(ChekinSDKContext);
  if (context === undefined) {
    throw new Error('useReservation must be used within a ChekinSDKProvider');
  }

  return context;
}

export {ChekinSDKContext, ChekinSDKProvider, useChekinSDK};
